import {
  flip,
  hide,
  offset,
  shift,
  size,
  useFloating,
  UseFloatingOptions,
} from '@floating-ui/react';
import { useMyEditorRef } from 'lib-knowledge-document-structure/plateHooks';
import { MyEditor } from 'lib-knowledge-document-structure/plateTypes';
import { BasePoint } from 'lib-knowledge-document-structure/slate/package';
import { useLayoutEffect, useRef } from 'react';

import {
  editorHeadersHeight,
  FloatingProps,
  getPointBoundingClientRect,
  useToggleAutoAttach,
} from './hoverUtils';

const getAnchorRect = ({
  editor,
  point,
  anchorElement,
}: {
  editor: MyEditor;
  point: BasePoint | null;
  anchorElement?: HTMLElement | null;
}) => {
  const getPointRect = getPointBoundingClientRect(editor, point);
  if (!anchorElement) return getPointRect;
  return () =>
    anchorElement.isConnected
      ? anchorElement.getBoundingClientRect()
      : getPointRect();
};

/**
 * Helps position an floating element near the editor selection.
 *
 * @returns
 */
const usePointHover = ({
  point,
  anchorElement,
  floatingDivId,
  onOpenChange,
}: {
  point: BasePoint | null;
  anchorElement?: HTMLElement | null;
  floatingDivId: string;
  onOpenChange?: UseFloatingOptions['onOpenChange'];
}): FloatingProps => {
  const editor = useMyEditorRef();
  const isOpen = !!point;
  const virtualElementRef = useRef({
    getBoundingClientRect: getAnchorRect({ editor, point, anchorElement }),
  });

  const floatingResult = useFloating({
    middleware: [
      offset(6),
      shift(),
      flip({
        padding: 150,
        fallbackPlacements: ['top-start'],
      }),
      size({
        padding: { top: editorHeadersHeight },
        apply({ availableHeight, elements }) {
          elements.floating.style.setProperty(
            '--floating-available-height',
            `${Math.max(availableHeight, 0)}px`,
          );
        },
      }),
      hide({
        padding: {
          top: editorHeadersHeight,
          left: 0,
          bottom: 0,
          right: 0,
        },
      }),
    ],
    strategy: 'fixed',
    placement: 'bottom-start',
    onOpenChange,
    open: isOpen,
  });

  const { update, refs, middlewareData } = floatingResult;

  useToggleAutoAttach({
    isOpen,
    update,
    floatingDivId,
    virtualElementRef: virtualElementRef.current,
  });

  // Only update if we have a point and are open
  useLayoutEffect(() => {
    if (isOpen && point) {
      virtualElementRef.current = {
        getBoundingClientRect: getAnchorRect({ editor, point, anchorElement }),
      };
      refs.setReference(virtualElementRef.current);
    }
  }, [anchorElement, editor, isOpen, point, refs]);

  return {
    ...floatingResult,
    isOpen,
    hide: middlewareData.hide?.referenceHidden || false,
  };
};

export default usePointHover;
